import React, { useEffect, useState } from 'react';
import { toast } from '../utils/toast';

const API = (process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app').replace(/\/+$/, '');
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.mela.marketplace';
const IOS_URL = 'https://apps.apple.com/in/app/mela-exhibition-marketplace/id6766425203';
const REASONS = ['Hard to use', 'Found a bug', 'Missing feature', 'Too slow'];

type Stage = 'sentiment' | 'happy' | 'feedback';

/**
 * Smart-gated rating sheet for the Mela consumer web app. Triggered by the
 * 'mela-rating-prompt' event (see utils/ratingPrompt.ts). Web has no native
 * store sheet, so "Loving it" links out to the stores (also a download nudge);
 * "Could be better" sends private feedback to POST /app-feedback. Mounted once
 * in App.tsx, alongside <MelaToastContainer/>.
 */
export default function MelaRatingPromptContainer() {
  const [visible, setVisible] = useState(false);
  const [stage, setStage] = useState<Stage>('sentiment');
  const [picked, setPicked] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onShow = () => { setStage('sentiment'); setPicked([]); setMessage(''); setVisible(true); };
    window.addEventListener('mela-rating-prompt', onShow);
    return () => window.removeEventListener('mela-rating-prompt', onShow);
  }, []);

  const close = () => setVisible(false);
  const toggle = (r: string) =>
    setPicked((p) => (p.includes(r) ? p.filter((x) => x !== r) : [...p, r]));

  const postFeedback = (body: { sentiment: string; reasons?: string[]; message?: string }) => {
    const token = localStorage.getItem('mela_token') || '';
    return fetch(`${API}/api/v1/mela/app-feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { 'X-Mela-Token': token } : {}) },
      body: JSON.stringify(body),
    });
  };

  const rateOnStore = (url: string) => {
    // Log who rated positively (username resolved server-side from the token);
    // the store star itself is anonymous. Best-effort.
    postFeedback({ sentiment: 'positive' }).catch(() => {});
    window.open(url, '_blank', 'noopener,noreferrer');
    toast('Thank you! 🙏', 'success');
    close();
  };

  const sendFeedback = async () => {
    setSending(true);
    try { await postFeedback({ sentiment: 'negative', reasons: picked, message }); }
    catch { /* best-effort */ }
    setSending(false);
    toast("Got it — thank you. We're on it. 🙏", 'success');
    close();
  };

  if (!visible) return null;

  // Inline v9 dark palette (matches the approved prototype)
  const C = { card: '#1A1510', input: '#221C14', border: '#3A2E20', saffron: '#E8881A',
    text: '#F5EDD8', text2: '#C8AD96', muted: '#9A8870' };

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  };
  const sheet: React.CSSProperties = {
    width: '100%', maxWidth: 440, background: C.card, color: C.text,
    borderRadius: '22px 22px 0 0', padding: '22px 22px 28px',
    boxShadow: '0 -20px 60px rgba(0,0,0,.5)', fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
    animation: 'melaRiseUp .28s cubic-bezier(.22,1,.36,1)',
  };
  const emoji: React.CSSProperties = { fontSize: 44, textAlign: 'center' };
  const title: React.CSSProperties = { fontSize: 22, fontWeight: 800, textAlign: 'center', margin: '6px 0 4px' };
  const msg: React.CSSProperties = { fontSize: 13.5, color: C.text2, textAlign: 'center', lineHeight: 1.5, margin: '0 6px 18px' };
  const btn: React.CSSProperties = { width: '100%', border: 'none', borderRadius: 13, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  const primary: React.CSSProperties = { ...btn, background: C.saffron, color: '#1a1206', marginTop: 10 };
  const secondary: React.CSSProperties = { ...btn, background: C.input, color: C.text, border: `1px solid ${C.border}`, marginTop: 10 };
  const choice: React.CSSProperties = { flex: 1, background: C.input, border: `1px solid ${C.border}`, borderRadius: 16, padding: '18px 8px', cursor: 'pointer', color: C.text, fontFamily: 'inherit' };
  const link: React.CSSProperties = { width: '100%', background: 'none', border: 'none', color: C.muted, fontSize: 12.5, cursor: 'pointer', marginTop: 14, fontFamily: 'inherit' };

  return (
    <div style={overlay} onClick={close}>
      <style>{`@keyframes melaRiseUp { from { transform: translateY(100%);} to { transform: translateY(0);} }`}</style>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ width: 38, height: 4, borderRadius: 4, background: C.border, margin: '0 auto 16px' }} />

        {stage === 'sentiment' && (
          <>
            <div style={emoji}>🪔</div>
            <div style={title}>Enjoying Mela so far?</div>
            <div style={msg}>Quick one — how's your experience been?</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button style={choice} onClick={() => setStage('happy')}>
                <div style={{ fontSize: 32 }}>😍</div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>Loving it</div>
              </button>
              <button style={choice} onClick={() => setStage('feedback')}>
                <div style={{ fontSize: 32 }}>😕</div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>Could be better</div>
              </button>
            </div>
            <button style={link} onClick={close}>Ask me later</button>
          </>
        )}

        {stage === 'happy' && (
          <>
            <div style={emoji}>🌟</div>
            <div style={title}>Yay! Mind rating us?</div>
            <div style={msg}>A quick rating on the app store helps others discover Mela.</div>
            <button style={primary} onClick={() => rateOnStore(ANDROID_URL)}>⭐ Rate on Google Play</button>
            <button style={secondary} onClick={() => rateOnStore(IOS_URL)}>⭐ Rate on the App Store</button>
            <button style={link} onClick={close}>Maybe later</button>
          </>
        )}

        {stage === 'feedback' && (
          <>
            <div style={emoji}>🙏</div>
            <div style={title}>Sorry — what went wrong?</div>
            <div style={msg}>Tell us and we'll fix it. This goes straight to the Mela team, not the public.</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {REASONS.map((r) => {
                const on = picked.includes(r);
                return (
                  <button key={r} onClick={() => toggle(r)} style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 12px', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit',
                    background: on ? C.saffron + '22' : C.input, color: on ? C.saffron : C.text2, border: `1px solid ${on ? C.saffron : C.border}` }}>
                    {r}
                  </button>
                );
              })}
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us more (optional)…"
              style={{ width: '100%', minHeight: 84, resize: 'none', background: C.input, color: C.text, border: `1px solid ${C.border}`, borderRadius: 13, padding: '12px 14px', fontFamily: 'inherit', fontSize: 13.5, outline: 'none' }} />
            <button style={{ ...primary, opacity: sending ? 0.6 : 1 }} disabled={sending} onClick={sendFeedback}>
              {sending ? 'Sending…' : 'Send feedback'}
            </button>
            <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 12 }}>🔒 Private — only the Mela team sees this.</div>
          </>
        )}
      </div>
    </div>
  );
}
